// Rechner: Regalböden / Fächer gleichmäßig verteilen.
// Schlichte, funktionierende Basis-UI (Eingabe -> calc.js -> Markierliste).
// Die interaktive SVG-Skizze kommt mit dem Claude-Design (Platzhalter unten).
import React, { useEffect, useMemo, useState } from "react";
import { calcShelves, fmtCm } from "@/lib/calc";
import { track, trackSettled } from "@/lib/track";
import { NumberField, Warnings, ResultRow, ResultActions, Steps } from "./shared";
import ShelfSketch from "./ShelfSketch";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const DEFAULTS = {
  innerHeight: "180",   // cm
  count: "5",
  thickness: "1.8",     // cm
  rounding: "0",        // mm: 0 = keine
};

const MODES = [
  { id: "compartments", label: "Anzahl Fächer" },
  { id: "shelves", label: "Anzahl Böden" },
];

const ROUNDINGS = [
  { id: "0", label: "exakt" },
  { id: "1", label: "1 mm" },
  { id: "5", label: "5 mm" },
  { id: "10", label: "1 cm" },
];

export default function ShelfCalculator({ initialMode = "compartments" }) {
  const [mode, setMode] = useState(initialMode);
  const [v, setV] = useState(DEFAULTS);
  const set = (k) => (val) => setV((s) => ({ ...s, [k]: val }));

  const r = useMemo(() => {
    const innerHeightMm = parseFloat(String(v.innerHeight).replace(",", ".")) * 10;
    const shelfThicknessMm = parseFloat(String(v.thickness).replace(",", ".")) * 10;
    const count = Math.round(parseFloat(String(v.count).replace(",", ".")));
    const roundingMm = v.rounding === "0" ? null : Number(v.rounding);
    return calcShelves({
      innerHeightMm,
      shelfThicknessMm,
      mode,
      compartmentCount: mode === "compartments" ? count : undefined,
      shelfCount: mode === "shelves" ? count : undefined,
      roundingMm,
    });
  }, [v, mode]);

  useEffect(() => {
    if (r.ok) {
      trackSettled("calculator_used", { calculator: "shelf", mode });
      trackSettled("result_shown", { calculator: "shelf", verdict: (r.warnings && r.warnings.length) ? "warn" : "ok" });
    }
  }, [r, mode]);

  const rounded = v.rounding !== "0";
  const ok = r.ok;

  const results = ok
    ? [
        { label: "Gleichmäßige Fachhöhe", value: fmtCm(r.compartmentHeightMm) },
        { label: "Anzahl Regalböden", value: String(r.shelfCount) },
        { label: "Anzahl Fächer", value: String(r.compartmentCount) },
        { label: "Höhe durch Böden", value: fmtCm(r.totalThicknessMm) },
        { label: "Verfügbare Fachhöhe gesamt", value: fmtCm(r.availableHeightMm) },
      ]
    : [];

  const markPos = (p) =>
    rounded && p.roundedBottomPositionMm != null ? fmtCm(p.roundedBottomPositionMm) : fmtCm(p.bottomPositionMm);

  const steps = ok
    ? r.shelfPositions.map((p) => `${p.shelfIndex}. Boden: Unterkante bei ${markPos(p)}`)
    : [];

  const copyText = ok
    ? [
        "Markiere ab Innen-Unterkante:",
        ...r.shelfPositions.map((p) => `${p.shelfIndex}. Boden: Unterkante bei ${markPos(p)}`),
      ].join("\n")
    : "";

  return (
    <Card className="p-4 sm:p-5 border" data-testid="calc-shelves">
      {/* Modus-Umschaltung: Fächer vs. Böden (häufigster Denkfehler) */}
      <div className="inline-flex rounded-lg border p-1 bg-secondary/40 mb-4">
        {MODES.map((m) => (
          <Button
            key={m.id}
            type="button"
            size="sm"
            variant={mode === m.id ? "default" : "ghost"}
            onClick={() => setMode(m.id)}
            data-testid={`mode-${m.id}`}
          >
            {m.label}
          </Button>
        ))}
      </div>

      <div className="grid sm:grid-cols-3 gap-4">
        <NumberField id="innerHeight" label="Lichte Innenhöhe" value={v.innerHeight} onChange={set("innerHeight")} step="0.1" />
        <NumberField
          id="count"
          label={mode === "compartments" ? "Anzahl Fächer" : "Anzahl Regalböden"}
          value={v.count}
          onChange={set("count")}
          unit=""
          step="1"
        />
        <NumberField id="thickness" label="Bodenstärke" value={v.thickness} onChange={set("thickness")} step="0.1" />
      </div>

      <div className="mt-4">
        <span className="text-sm font-medium">Rundung der Markiermaße</span>
        <div className="inline-flex rounded-lg border p-1 bg-secondary/40 ml-3">
          {ROUNDINGS.map((opt) => (
            <Button
              key={opt.id}
              type="button"
              size="sm"
              variant={v.rounding === opt.id ? "default" : "ghost"}
              onClick={() => set("rounding")(opt.id)}
              data-testid={`rounding-${opt.id}`}
            >
              {opt.label}
            </Button>
          ))}
        </div>
      </div>

      {/* Live-Skizze des Regals: aktualisiert sich mit jeder Eingabe. */}
      <div className="mt-5 rounded-xl border bg-muted/20 p-4" data-testid="sketch">
        <ShelfSketch result={r} rounded={rounded} />
      </div>

      {!ok && r.errors && r.errors.length > 0 && (
        <div className="mt-4 rounded-xl border border-destructive/40 bg-destructive/10 p-3 text-sm" role="alert" data-testid="calc-errors">
          {r.errors.map((e, i) => (
            <p key={i}>{e}</p>
          ))}
        </div>
      )}

      {ok && (
        <div className="mt-5">
          <Warnings items={r.warnings} />
          <div className="mt-3" data-testid="calc-results">
            {results.map((row) => (
              <ResultRow key={row.label} label={row.label} value={row.value} />
            ))}
          </div>
          <h3 className="mt-5 font-heading font-semibold text-sm">Markierliste (ab Innen-Unterkante)</h3>
          <Steps items={steps} />
          <ResultActions text={copyText} />
        </div>
      )}
    </Card>
  );
}
